import {moment} from "@/src/modules/core/lib";
import {normalizeUsPhoneDigits} from "@/src/modules/properties/lib/us-phone";
import type {Household} from "@/src/modules/households/types";

import {
  hasSafelistingLimitOverride,
  isSafelistingDaysInARowOverride,
  isSafelistingExcludedFromQuota,
} from "./safelisting-quota-display";
import type {DatesRange, Safelisting, SafelistingFormValues} from "../types";

export function getSafelistingFormDefaultValues(
  safelisting?: Safelisting | null,
): SafelistingFormValues {
  const defaultDatesRange: DatesRange = {from: undefined, to: undefined};

  if (!safelisting) {
    return {
      household: null,
      dates_range: defaultDatesRange,
      recipient_name: "",
      recipient_email: "",
      recipient_phone: "",
      notes: "",
      private_notes: "",
      excluded_dates: [],
      override_limit: false,
      override_count_toward_total: false,
      override_days_in_a_row: false,
    };
  }

  const householdId = safelisting.household?.house_hold_id ?? 0;
  const householdStub = {
    house_hold_id: householdId,
    name: safelisting.household?.name ?? "",
  };

  const days = safelisting.days ?? [];
  const sortedDays = [...days].sort((a, b) => a.date.localeCompare(b.date));

  let from: Date | undefined;
  let to: Date | undefined;
  if (sortedDays.length > 0) {
    from = moment(sortedDays[0]!.date, "YYYY-MM-DD").startOf("day").toDate();
    to = moment(sortedDays[sortedDays.length - 1]!.date, "YYYY-MM-DD").startOf("day").toDate();
  }
  const dates_range: DatesRange = {from, to};

  const dayDatesSet = new Set(days.map((d) => d.date));
  const excluded_dates: string[] = [];
  if (sortedDays.length > 0) {
    const cursor = moment(sortedDays[0]!.date);
    const end = moment(sortedDays[sortedDays.length - 1]!.date);
    while (cursor.isSameOrBefore(end, "day")) {
      const dateStr = cursor.format("YYYY-MM-DD");
      if (!dayDatesSet.has(dateStr)) excluded_dates.push(dateStr);
      cursor.add(1, "day");
    }
  }

  return {
    household: householdStub,
    dates_range,
    recipient_name: safelisting.recipient_name ?? "",
    recipient_email: safelisting.recipient_email ?? "",
    recipient_phone: normalizeUsPhoneDigits(safelisting.recipient_phone ?? ""),
    notes: safelisting.notes ?? "",
    private_notes: safelisting.private_notes ?? "",
    excluded_dates,
    override_limit: hasSafelistingLimitOverride(safelisting),
    override_reason: safelisting.override_reason ?? "",
    override_count_toward_total: isSafelistingExcludedFromQuota(safelisting),
    override_days_in_a_row: isSafelistingDaysInARowOverride(safelisting.override_days_in_a_row),
  };
}
